import { useAuth } from "@clerk/react";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ApiError } from "@/features/api/apiClient";
import {
  createAdminPromoCode,
  listAdminPromoCodes,
  type AdminPromoCodeCreateInput,
  type AdminPromoCodeListResponse,
  type AdminPromoCodeSummary,
} from "@/features/admin/adminApi";

const TIER_OPTIONS = [
  { value: "standard", label: "Standard", description: "5 blueprints/month, up to 5 projects" },
  { value: "lifetime_standard", label: "Lifetime Standard", description: "Lifetime Standard access" },
  { value: "pro", label: "Pro", description: "25 blueprints/month, Pro tools, PDF export" },
];

const PAGE_SIZE = 20;

function formatDate(value: string | null): string {
  if (!value) return "—";
  return new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(new Date(value));
}

function statusBadgeClass(status: AdminPromoCodeSummary["statusLabel"]): string {
  switch (status) {
    case "Active":
      return "bg-emerald-100 text-emerald-800";
    case "Inactive":
      return "bg-slate-100 text-slate-600";
    case "Expired":
      return "bg-amber-100 text-amber-800";
    case "Exhausted":
      return "bg-orange-100 text-orange-800";
  }
}

function tierLabel(tier: string): string {
  if (tier === "lifetime_standard") return "Lifetime Standard";
  return tier.charAt(0).toUpperCase() + tier.slice(1);
}

function PromoCodeCard({ code }: { code: AdminPromoCodeSummary }) {
  const usageText =
    code.maxUses !== null
      ? `${code.usesCount} / ${code.maxUses} uses`
      : `${code.usesCount} uses`;

  return (
    <article className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-mono text-base font-semibold text-slate-950">
              {code.code}
            </span>
            <span
              className={`rounded-full px-2 py-0.5 text-xs font-semibold ${statusBadgeClass(code.statusLabel)}`}
            >
              {code.statusLabel}
            </span>
            <span className="rounded bg-indigo-100 px-2 py-0.5 text-xs font-semibold text-indigo-800">
              {tierLabel(code.tierGranted)}
            </span>
          </div>
          <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm text-slate-600">
            <span>{usageText}</span>
            {code.accessDurationDays ? (
              <span>{code.accessDurationDays} days access</span>
            ) : null}
            {code.expiresAt ? (
              <span>Expires {formatDate(code.expiresAt)}</span>
            ) : null}
            <span>Created {formatDate(code.createdAt)}</span>
          </div>
        </div>
        <Link
          to={`/admin/promo-codes/${encodeURIComponent(code.id)}`}
          className="inline-flex w-full items-center justify-center rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-indigo-700 sm:w-fit sm:flex-shrink-0"
        >
          View
        </Link>
      </div>
    </article>
  );
}

function CreatePromoCodeForm({ onCreated }: { onCreated: () => void }) {
  const { getToken } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [code, setCode] = useState("");
  const [tier, setTier] = useState<"standard" | "lifetime_standard" | "pro">("standard");
  const [durationDays, setDurationDays] = useState("183");
  const [maxUses, setMaxUses] = useState("");
  const [expiresAt, setExpiresAt] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const showDuration = tier !== "lifetime_standard";

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);
    setSuccess(null);

    const input: AdminPromoCodeCreateInput = {
      code: code.trim(),
      tierGranted: tier,
      accessDurationDays: showDuration && durationDays ? Number(durationDays) : null,
      maxUses: maxUses ? Number(maxUses) : null,
      expiresAt: expiresAt || null,
      active: true,
    };

    try {
      const token = await getToken();
      const created = await createAdminPromoCode(token, input);
      setSuccess(`Access code "${created.code}" created successfully.`);
      setCode("");
      setDurationDays("183");
      setMaxUses("");
      setExpiresAt("");
      onCreated();
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : "Failed to create access code.",
      );
    } finally {
      setIsSubmitting(false);
    }
  }

  if (!isOpen) {
    return (
      <button
        type="button"
        onClick={() => setIsOpen(true)}
        className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-indigo-700"
      >
        Create access code
      </button>
    );
  }

  return (
    <section className="rounded-lg border border-indigo-200 bg-indigo-50 p-5">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm font-semibold text-indigo-900">Create access code</p>
        <button
          type="button"
          onClick={() => { setIsOpen(false); setError(null); setSuccess(null); }}
          className="text-xs text-indigo-600 hover:text-indigo-900"
        >
          Cancel
        </button>
      </div>
      <p className="mt-1 text-xs text-indigo-700">
        Access codes grant product access without processing a payment. Use them
        for beta testers, support fixes, VIP access and approved promotions.
      </p>

      {error ? (
        <div className="mt-3 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
          {error}
        </div>
      ) : null}
      {success ? (
        <div className="mt-3 rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800">
          {success}
        </div>
      ) : null}

      <form onSubmit={handleSubmit} className="mt-4 space-y-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className="block text-xs font-medium text-slate-700">
              Code <span className="text-red-500">*</span>
            </label>
            <input
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              placeholder="BETA2024"
              maxLength={50}
              required
              className="mt-1 w-full rounded-md border border-slate-300 bg-white px-3 py-2 font-mono text-sm text-slate-950 uppercase outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
            />
            <p className="mt-0.5 text-xs text-slate-400">
              A–Z, 0–9, hyphens and underscores only (4–50 chars)
            </p>
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-700">
              Access granted <span className="text-red-500">*</span>
            </label>
            <select
              value={tier}
              onChange={(e) => setTier(e.target.value as typeof tier)}
              className="mt-1 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-950 outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
            >
              {TIER_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
            <p className="mt-0.5 text-xs text-slate-400">
              {TIER_OPTIONS.find((o) => o.value === tier)?.description}
            </p>
          </div>

          {showDuration ? (
            <div>
              <label className="block text-xs font-medium text-slate-700">
                Duration (days)
              </label>
              <input
                type="number"
                value={durationDays}
                onChange={(e) => setDurationDays(e.target.value)}
                placeholder={tier === "standard" ? "183 (default)" : "Optional"}
                min={1}
                max={3660}
                className="mt-1 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-950 outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
              />
              <p className="mt-0.5 text-xs text-slate-400">
                {tier === "standard" ? "Default 183 days if left blank" : "Leave blank for indefinite pro access"}
              </p>
            </div>
          ) : null}

          <div>
            <label className="block text-xs font-medium text-slate-700">
              Max uses
            </label>
            <input
              type="number"
              value={maxUses}
              onChange={(e) => setMaxUses(e.target.value)}
              placeholder="Unlimited"
              min={1}
              className="mt-1 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-950 outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
            />
            <p className="mt-0.5 text-xs text-slate-400">
              Leave blank for unlimited uses
            </p>
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-700">
              Expiry date
            </label>
            <input
              type="date"
              value={expiresAt}
              onChange={(e) => setExpiresAt(e.target.value)}
              className="mt-1 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-950 outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
            />
            <p className="mt-0.5 text-xs text-slate-400">
              Leave blank for no expiry
            </p>
          </div>
        </div>

        <button
          type="submit"
          disabled={isSubmitting || !code.trim()}
          className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {isSubmitting ? "Creating…" : "Create access code"}
        </button>
      </form>
    </section>
  );
}

function AccessDenied() {
  return (
    <section className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
      <p className="text-sm font-semibold text-slate-500">Not found</p>
      <h2 className="mt-2 text-2xl font-semibold tracking-normal text-slate-950">
        This page is not available
      </h2>
      <p className="mt-3 max-w-2xl text-sm leading-6 text-slate-600">
        Access code management is only available to authorised operators.
      </p>
    </section>
  );
}

export function AdminPromoCodes() {
  const { getToken, isLoaded } = useAuth();
  const [query, setQuery] = useState("");
  const [submittedQuery, setSubmittedQuery] = useState("");
  const [tierFilter, setTierFilter] = useState("");
  const [activeFilter, setActiveFilter] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState<AdminPromoCodeListResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isDenied, setIsDenied] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const filters = useMemo(
    () => ({
      q: submittedQuery,
      tier: tierFilter,
      active:
        activeFilter === "true" ? true : activeFilter === "false" ? false : undefined,
      page,
      pageSize: PAGE_SIZE,
    }),
    [page, submittedQuery, tierFilter, activeFilter],
  );

  useEffect(() => {
    async function loadCodes() {
      if (!isLoaded) return;

      setIsLoading(true);
      setIsDenied(false);
      setError(null);

      try {
        const token = await getToken();
        setData(await listAdminPromoCodes(token, filters));
      } catch (err) {
        if (
          err instanceof ApiError &&
          (err.status === 401 || err.status === 403 || err.status === 404)
        ) {
          setIsDenied(true);
          return;
        }
        setError(
          err instanceof Error ? err.message : "Unable to load access codes.",
        );
      } finally {
        setIsLoading(false);
      }
    }

    void loadCodes();
  }, [filters, getToken, isLoaded, refreshKey]);

  if (isDenied) {
    return <AccessDenied />;
  }

  return (
    <div className="space-y-6">
      <Link
        to="/admin"
        className="inline-flex text-sm font-semibold text-indigo-700 hover:text-indigo-900"
      >
        Back to Admin
      </Link>

      <section className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
        <p className="text-sm font-semibold text-indigo-700">Admin</p>
        <h2 className="mt-2 text-2xl font-semibold tracking-normal text-slate-950">
          Access Codes
        </h2>
        <p className="mt-3 max-w-3xl text-sm leading-6 text-slate-600">
          Create and manage access codes for beta users, support fixes, VIP
          access and approved promotions.
        </p>
      </section>

      <CreatePromoCodeForm onCreated={() => setRefreshKey((k) => k + 1)} />

      <section className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
        <form
          className="grid gap-3 lg:grid-cols-[1fr_160px_160px_auto]"
          onSubmit={(e) => {
            e.preventDefault();
            setPage(1);
            setSubmittedQuery(query.trim());
          }}
        >
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by code…"
            className="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-950 outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
          />
          <select
            value={tierFilter}
            onChange={(e) => { setPage(1); setTierFilter(e.target.value); }}
            className="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-950 outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
          >
            <option value="">All tiers</option>
            <option value="standard">Standard</option>
            <option value="lifetime_standard">Lifetime Standard</option>
            <option value="pro">Pro</option>
          </select>
          <select
            value={activeFilter}
            onChange={(e) => { setPage(1); setActiveFilter(e.target.value); }}
            className="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-950 outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
          >
            <option value="">All statuses</option>
            <option value="true">Active</option>
            <option value="false">Inactive</option>
          </select>
          <button
            type="submit"
            className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-indigo-700"
          >
            Search
          </button>
        </form>
      </section>

      {error ? (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">
          {error}
        </div>
      ) : null}

      {isLoading ? (
        <div className="h-72 animate-pulse rounded-lg bg-white" />
      ) : (
        <section className="grid gap-3">
          {(data?.codes ?? []).map((code) => (
            <PromoCodeCard key={code.id} code={code} />
          ))}

          {data?.codes.length === 0 ? (
            <div className="rounded-lg border border-dashed border-slate-300 bg-white p-8 text-center">
              <p className="font-semibold text-slate-950">No access codes found</p>
              <p className="mt-2 text-sm text-slate-600">
                Create your first access code above.
              </p>
            </div>
          ) : null}
        </section>
      )}

      <div className="flex items-center justify-between gap-3">
        <button
          type="button"
          disabled={page <= 1 || isLoading}
          onClick={() => setPage((p) => Math.max(1, p - 1))}
          className="rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50"
        >
          Previous
        </button>
        <p className="text-sm text-slate-600">Page {page}</p>
        <button
          type="button"
          disabled={!data?.pagination.hasNextPage || isLoading}
          onClick={() => setPage((p) => p + 1)}
          className="rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50"
        >
          Next
        </button>
      </div>
    </div>
  );
}
